<template>
  <div>
    <!-- Filtros -->
    <b-card class="mb-2">
      <b-card-header><h5 class="mb-0">Livro Razão</h5></b-card-header>
      <b-card-body>
        <b-row>
          <b-col cols="12" md="3">
            <b-form-group label="Data Inicial">
              <b-form-input v-model="filtro.dataInicio" type="date" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="3">
            <b-form-group label="Data Final">
              <b-form-input v-model="filtro.dataFim" type="date" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4">
            <b-form-group label="Plano de Conta">
              <v-select
                v-model="filtro.planoContaId"
                :options="comboPlanoConta"
                :reduce="o => o.id"
                :get-option-label="o => `${o.codNatureza || ''} - ${o.classe || ''}`"
                placeholder="Todos"
                clearable
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="2" class="d-flex align-items-end">
            <b-button variant="primary" class="w-100" :disabled="loading" @click="buscar">
              <b-spinner v-if="loading" small class="mr-1" />
              <feather-icon v-else icon="SearchIcon" class="mr-1" />
              Filtrar
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Tabela -->
    <b-card v-if="dados">
      <b-card-header class="d-flex justify-content-between align-items-center">
        <div>
          <h5 class="mb-0">Livro Razão</h5>
          <small class="text-muted">{{ formatDate(dados.dataInicio) }} a {{ formatDate(dados.dataFim) }} | {{ dados.totalRegistros }} registros</small>
        </div>
        <div class="d-flex">
          <b-button variant="success" size="sm" @click="exportarCsv">
            <feather-icon icon="DownloadIcon" class="mr-1" />CSV
          </b-button>
          <b-button variant="danger" size="sm" class="ml-1" @click="imprimirPdf" :disabled="loadingPdf">
            <b-spinner v-if="loadingPdf" small class="mr-1" />
            <feather-icon v-else icon="PrinterIcon" class="mr-1" />PDF
          </b-button>
        </div>
      </b-card-header>
      <b-card-body>
        <b-table :items="dados.transacoes" :fields="campos" striped responsive hover small>
          <template #cell(dataLancamento)="{ item }">{{ formatDate(item.dataLancamento) }}</template>
          <template #cell(tipo)="{ item }">
            <b-badge :variant="item.tipo === 'RECEITA' ? 'light-success' : 'light-danger'">
              {{ item.tipo === 'RECEITA' ? 'Receita' : 'Despesa' }}
            </b-badge>
          </template>
          <template #cell(valorTotal)="{ item }">
            <span :class="item.tipo === 'RECEITA' ? 'text-success' : 'text-danger'">
              {{ formatMoeda(item.valorTotal) }}
            </span>
          </template>
          <template #cell(situacao)="{ item }">
            <b-badge :variant="badgeSituacao(item.situacao)">{{ item.situacao }}</b-badge>
          </template>
          <template #custom-foot>
            <b-tr class="font-weight-bold table-active">
              <b-td colspan="5">TOTAL RECEITAS</b-td>
              <b-td class="text-success text-right">{{ formatMoeda(dados.totalReceitas) }}</b-td>
              <b-td colspan="3" />
            </b-tr>
            <b-tr class="font-weight-bold table-active">
              <b-td colspan="5">TOTAL DESPESAS</b-td>
              <b-td class="text-danger text-right">{{ formatMoeda(dados.totalDespesas) }}</b-td>
              <b-td colspan="3" />
            </b-tr>
            <b-tr class="font-weight-bold table-active">
              <b-td colspan="5">SALDO</b-td>
              <b-td :class="dados.saldo >= 0 ? 'text-success text-right' : 'text-danger text-right'">{{ formatMoeda(dados.saldo) }}</b-td>
              <b-td colspan="3" />
            </b-tr>
          </template>
        </b-table>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BCardHeader, BCardBody, BRow, BCol, BFormGroup, BFormInput,
  BButton, BSpinner, BTable, BTr, BTd, BBadge
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import contabilServices from '@/api/financialModule/contabilServices'
import mixinsGlobal from '@/mixins'

export default {
  name: 'LivroRazaoReport',
  components: { BCard, BCardHeader, BCardBody, BRow, BCol, BFormGroup, BFormInput, BButton, BSpinner, BTable, BTr, BTd, BBadge, vSelect },
  mixins: [mixinsGlobal],
  data() {
    const hoje = new Date()
    return {
      filtro: {
        dataInicio: `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-01`,
        dataFim: hoje.toISOString().substring(0, 10),
        planoContaId: null
      },
      dados: null,
      loading: false,
      loadingPdf: false,
      comboPlanoConta: [],
      campos: [
        { key: 'dataLancamento', label: 'Data' },
        { key: 'numero', label: 'Nº' },
        { key: 'tipo', label: 'Tipo' },
        { key: 'planoContaNome', label: 'Plano de Conta' },
        { key: 'descricao', label: 'Descrição' },
        { key: 'valorTotal', label: 'Valor', tdClass: 'text-right' },
        { key: 'situacao', label: 'Situação' },
        { key: 'contaNome', label: 'Conta' },
        { key: 'entidadeNome', label: 'Entidade' }
      ]
    }
  },
  mounted() {
    this.carregarPlanoConta()
  },
  methods: {
    async carregarPlanoConta() {
      try {
        const res = await this.$store.dispatch('combosModule/loadComboPlanoContas')
        this.comboPlanoConta = res?.data || []
      } catch {}
    },
    async buscar() {
      this.loading = true
      try {
        const res = await contabilServices.getLivroRazao({
          dataInicio: this.filtro.dataInicio,
          dataFim: this.filtro.dataFim,
          planoContaId: this.filtro.planoContaId || undefined
        })
        this.dados = res.data
      } catch {
        this.MensagemError('Erro', 'Falha ao gerar Livro Razão.')
      } finally { this.loading = false }
    },
    formatDate(d) {
      if (!d) return ''
      const parts = Array.isArray(d) ? d : String(d).split('-')
      return `${String(parts[2]).padStart(2,'0')}/${String(parts[1]).padStart(2,'0')}/${parts[0]}`
    },
    formatMoeda(v) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
    },
    badgeSituacao(s) {
      const map = { ABERTO: 'light-info', VENCIDO: 'light-warning', QUITADO: 'light-success', PARCIAL: 'light-primary', CANCELADO: 'light-secondary' }
      return map[s] || 'light-secondary'
    },
    exportarCsv() {
      if (!this.dados) return
      const h = ['Data', 'Nº', 'Tipo', 'Plano de Conta', 'Descrição', 'Valor', 'Situação', 'Conta', 'Entidade']
      const rows = this.dados.transacoes.map(t => [
        this.formatDate(t.dataLancamento), t.numero, t.tipo, t.planoContaNome, t.descricao, t.valorTotal, t.situacao, t.contaNome, t.entidadeNome
      ])
      const csv = [h, ...rows].map(r => r.join(';')).join('\n')
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a'); a.href = url; a.download = 'livro_razao.csv'; a.click()
    },
    imprimirPdf() {
      if (!this.dados) return
      this.loadingPdf = true
      this.$store.dispatch('reportModule/printReportRazaoLivro', {
        ...this.dados,
        planoContaFiltro: this.comboPlanoConta.find(p => p.id === this.filtro.planoContaId)?.classe || 'Todos os Planos de Conta'
      }).then(res => {
        if (res.error) this.MensagemError('Erro', res.error)
      }).finally(() => {
        this.loadingPdf = false
      })
    }
  }
}
</script>
